import {
  useRef,
  useEffect,
  useCallback,
  useMemo
} from 'react';

/**
 * @param {Function} onIntersect Function to call once intersected
 * @param {Object} optionsData Options object used to initialize IntersectionObserver
 * @param {boolean} onlyOnce Whether to stop observing after onIntersect is fired once
 *
 * @returns {Function} A callback ref function. Assign this to the element you want to observe.
 *
 * Usage:
 * const Component = () => {
 *   const targetRef = useIntersect(() => console.log('impressed!'));
 *
 *   return <div ref={targetRef}>Something here</div>
 * }
 */

const useIntersect = (onIntersect, optionsData, onlyOnce = true) => {
  const {
    root = null,
    rootMargin = '0px',
    threshold,
    delayMs = 0
  } = optionsData || {};

  const intersected = useRef(false);
  const observer = useRef(null);
  const timerRef = useRef(null);
  const callbackRef = useRef(onIntersect);
  const delayMsRef = useRef(delayMs);
  delayMsRef.current = delayMs;

  // Memoize by value so an inline object from the caller (e.g. { delayMs: 3000 })
  // doesn't cause the observer to disconnect/reconnect on every render.
  // threshold can be an array so stringify it for stable comparison.
  const observerOptions = useMemo(() =>
    ({
      root,
      rootMargin,
      ...(threshold !== undefined ? { threshold } : {})
    }), [root, rootMargin, JSON.stringify(threshold)]);

  const handleIntersect = useCallback(
    (entries) => {
      const isIntersecting = (entries[0] && entries[0].isIntersecting) || false;

      // Clear any existing timer
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const triggeredCallback = () => {
        if (observer.current) {
          callbackRef.current();

          if (!intersected.current && onlyOnce) {
            observer.current.disconnect();
            observer.current = null;
            intersected.current = true;
          }
        }
      };

      // If element is intersecting, set a timer for delayMs
      if (isIntersecting && delayMsRef.current > 0) {
        timerRef.current = window.setTimeout(triggeredCallback, delayMsRef.current);
      } else if (isIntersecting) {
        // If no delay required, call the callback immediately
        triggeredCallback();
      }
    },
    [onlyOnce]
  );

  useEffect(() => {
    callbackRef.current = onIntersect;
  }, [onIntersect]);

  // Cleanup on unmount
  useEffect(() =>
    () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
    }, []);

  // This function connects the ref to the element we want to observe.
  // Memoized so React sees the same function reference across renders,
  // preventing null-then-element re-calls that could re-create the observer.
  return useCallback((element) => {
    if (!element) {
      // React calls the ref with null when the element is removed from the DOM
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
      return;
    }

    // Don't re-attach after onlyOnce has already fired
    if (onlyOnce && intersected.current) {
      return;
    }

    if (!observer.current) {
      observer.current = new window.IntersectionObserver(handleIntersect, observerOptions);
    }
    observer.current.observe(element);
  }, [handleIntersect, observerOptions, onlyOnce]);
};

export default useIntersect;
